import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  addPartnerDealerAccountNote,
  fetchPartnerDealerAccountProfile,
  updatePartnerDealer,
} from "../../services/automapPartnerDealerApi";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import "../../styles/partnerDealers.css";

const NOTE_TYPES = ["general", "sales", "onboarding", "support", "verification", "trial", "risk"];

function formatCop(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  if (Number(value) === 0) return "Free";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(Number(value));
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function CompletenessRing({ score }) {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div className="dealer-profile__ring" aria-label={`Profile ${clamped}% complete`}>
      <svg viewBox="0 0 100 100" role="img" aria-hidden="true">
        <circle className="dealer-profile__ring-track" cx="50" cy="50" r={radius} />
        <circle
          className="dealer-profile__ring-progress"
          cx="50"
          cy="50"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="dealer-profile__ring-label">
        <strong>{clamped}%</strong>
        <span>Complete</span>
      </div>
    </div>
  );
}

function StatusBadge({ children, tone = "neutral" }) {
  return <span className={`dealer-profile__badge dealer-profile__badge--${tone}`}>{children}</span>;
}

export default function PartnerDealerAccountProfilePage() {
  const { dealerId: accountId } = useParams();
  const { path } = usePartnerPortalBase();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [salesOwner, setSalesOwner] = useState("");
  const [savingOwner, setSavingOwner] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteType, setNoteType] = useState("general");
  const [savingNote, setSavingNote] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchPartnerDealerAccountProfile(accountId);
      setProfile(result.profile);
      setSalesOwner(result.profile?.crmDealer?.assignedSalesOwner || "");
    } catch (err) {
      setProfile(null);
      setError(err.message || "Could not load dealer account.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveSalesOwner = async () => {
    if (!profile?.crmDealerId) return;
    try {
      setSavingOwner(true);
      setMessage("");
      setError("");
      await updatePartnerDealer(profile.crmDealerId, { assignedSalesOwner: salesOwner.trim() || null });
      setMessage("Account representative updated.");
      await load();
    } catch (err) {
      setError(err.message || "Could not update account representative.");
    } finally {
      setSavingOwner(false);
    }
  };

  const submitNote = async (event) => {
    event.preventDefault();
    if (!noteBody.trim()) return;
    try {
      setSavingNote(true);
      setMessage("");
      setError("");
      await addPartnerDealerAccountNote(accountId, {
        body: noteBody.trim(),
        noteType,
      });
      setNoteBody("");
      setMessage("Note added.");
      await load();
    } catch (err) {
      setError(err.message || "Could not save note.");
    } finally {
      setSavingNote(false);
    }
  };

  if (loading) {
    return (
      <div className="dealer-profile">
        <div className="dealer-profile__state">Loading dealer account…</div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="dealer-profile">
        <div className="dealer-profile__state dealer-profile__state--error">{error}</div>
        <Link className="dealer-profile__back-link" to={path("dealers")}>
          Back to dealer accounts
        </Link>
      </div>
    );
  }

  const account = profile?.account;
  const inventoryDealerId = profile?.inventory?.primaryDealerId || accountId;
  const completeness = profile?.profileCompleteness;
  const billing = profile?.billing;
  const purchasing = profile?.purchasing;
  const crmDealerId = profile?.crmDealerId;

  return (
    <div className="dealer-profile">
      <header className="dealer-profile__header">
        <div className="dealer-profile__header-main">
          <Link className="dealer-profile__back-link" to={path("dealers")}>
            ← Dealer accounts
          </Link>
          <div className="dealer-profile__title-row">
            <h1>{account?.businessName || "Dealer account"}</h1>
            <div className="dealer-profile__badges">
              {account?.hasCrmRecord ? (
                <StatusBadge tone="crm">CRM</StatusBadge>
              ) : (
                <StatusBadge tone="warning">Needs CRM</StatusBadge>
              )}
              <StatusBadge tone={account?.status === "active" ? "success" : "neutral"}>
                {account?.status || "unknown"}
              </StatusBadge>
              {billing?.owesMoney ? <StatusBadge tone="danger">Balance due</StatusBadge> : null}
            </div>
          </div>
          {account?.dealerGroupName && account.dealerGroupName !== account.businessName ? (
            <p className="dealer-profile__subtitle">{account.dealerGroupName}</p>
          ) : null}
        </div>
        <div className="dealer-profile__header-actions">
          <Link className="dealer-profile__btn dealer-profile__btn--primary" to={path(`dealers/${inventoryDealerId}/inventory`)}>
            View inventory
          </Link>
          <Link className="dealer-profile__btn" to={path(`dealers/${inventoryDealerId}/inventory/new`)}>
            Add listing
          </Link>
          {crmDealerId ? (
            <Link className="dealer-profile__btn" to={path(`dealers/${crmDealerId}/account`)}>
              Dealer portal
            </Link>
          ) : null}
        </div>
      </header>

      {error ? <div className="dealer-profile__alert dealer-profile__alert--error">{error}</div> : null}
      {message ? <div className="dealer-profile__alert dealer-profile__alert--success">{message}</div> : null}

      <div className="dealer-profile__grid">
        <section className="dealer-profile__card dealer-profile__card--highlight">
          <div className="dealer-profile__card-head">
            <h2>Profile completeness</h2>
            <span className="dealer-profile__muted">
              {completeness?.requiredCompletedCount}/{completeness?.requiredTotalCount} required items
            </span>
          </div>
          <div className="dealer-profile__completeness">
            <CompletenessRing score={completeness?.score} />
            <ul className="dealer-profile__checklist">
              {(completeness?.items || []).map((item) => (
                <li
                  key={item.key}
                  className={`dealer-profile__checklist-item${item.complete ? " is-complete" : ""}${
                    item.required ? " is-required" : ""
                  }`}
                >
                  <span className="dealer-profile__checkmark" aria-hidden="true">
                    {item.complete ? "✓" : "○"}
                  </span>
                  <span>{item.label}</span>
                  {item.required ? <span className="dealer-profile__required-tag">Required</span> : null}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="dealer-profile__card">
          <h2>Plan & billing</h2>
          {purchasing?.currentPlan ? (
            <>
              <div className="dealer-profile__plan-head">
                <div>
                  <p className="dealer-profile__plan-name">{purchasing.currentPlan.name}</p>
                  <p className="dealer-profile__muted">{purchasing.currentPlan.description}</p>
                </div>
                <div className="dealer-profile__plan-price">
                  {formatCop(purchasing.currentPlan.monthlyPriceCop)}
                  <span>/ month</span>
                </div>
              </div>
              <dl className="dealer-profile__metrics">
                <div>
                  <dt>Billing status</dt>
                  <dd>{billing?.statusLabel || "—"}</dd>
                </div>
                <div>
                  <dt>Amount owed</dt>
                  <dd className={billing?.owesMoney ? "is-danger" : undefined}>
                    {billing?.owesMoney ? formatCop(billing.amountOwedCop) : "Nothing due"}
                  </dd>
                </div>
                <div>
                  <dt>Listings</dt>
                  <dd>
                    {purchasing.usage?.activeListings ?? account?.activeListingCount ?? 0}
                    {purchasing.currentPlan.listingLimit != null
                      ? ` / ${purchasing.currentPlan.listingLimit}`
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt>Leads this month</dt>
                  <dd>
                    {purchasing.usage?.leadsThisMonth ?? 0}
                    {purchasing.currentPlan.leadLimitMonthly != null
                      ? ` / ${purchasing.currentPlan.leadLimitMonthly}`
                      : ""}
                  </dd>
                </div>
              </dl>
              {billing?.trialStatus ? (
                <p className="dealer-profile__muted">
                  Trial: {billing.trialStatus}
                  {billing.trialDaysRemaining != null ? ` · ${billing.trialDaysRemaining} days left` : ""}
                </p>
              ) : null}
            </>
          ) : (
            <p className="dealer-profile__muted">
              Plan and billing details appear once this account has a CRM record.
            </p>
          )}
        </section>

        <section className="dealer-profile__card">
          <h2>What they are purchasing</h2>
          {purchasing?.currentPlan ? (
            <>
              <ul className="dealer-profile__feature-list">
                {(purchasing.features || []).map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              {purchasing.pendingUpgradeRequest ? (
                <div className="dealer-profile__upgrade">
                  <strong>Pending upgrade</strong>
                  <p>
                    Requested {purchasing.pendingUpgradeRequest.requestedPlanKey || "plan change"}
                    {purchasing.pendingUpgradeRequest.createdAt
                      ? ` · ${formatDate(purchasing.pendingUpgradeRequest.createdAt)}`
                      : ""}
                  </p>
                </div>
              ) : null}
              {(purchasing.warnings || []).length ? (
                <ul className="dealer-profile__warnings">
                  {purchasing.warnings.map((warning) => (
                    <li key={`${warning.type}-${warning.message}`}>{warning.message}</li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : (
            <p className="dealer-profile__muted">No active subscription package on file yet.</p>
          )}
        </section>

        <section className="dealer-profile__card">
          <h2>Inventory access</h2>
          <dl className="dealer-profile__metrics">
            <div>
              <dt>Active listings</dt>
              <dd>{profile?.inventory?.activeListingCount ?? 0}</dd>
            </div>
            <div>
              <dt>Profiles</dt>
              <dd>{profile?.inventory?.profileCount ?? 0}</dd>
            </div>
            <div>
              <dt>Data sources</dt>
              <dd>{profile?.inventory?.dataSourceCount ?? 0}</dd>
            </div>
          </dl>
          <div className="dealer-profile__inline-actions">
            <Link className="dealer-profile__btn dealer-profile__btn--primary" to={path(`dealers/${inventoryDealerId}/inventory`)}>
              Open inventory
            </Link>
            <Link className="dealer-profile__btn" to={path(`dealers/${inventoryDealerId}/inventory/new`)}>
              Edit / add listings
            </Link>
          </div>
        </section>

        <section className="dealer-profile__card">
          <h2>Account representative</h2>
          {crmDealerId ? (
            <div className="dealer-profile__rep-form">
              <label htmlFor="assigned-sales-owner">Assigned representative</label>
              <div className="dealer-profile__rep-row">
                <input
                  id="assigned-sales-owner"
                  type="text"
                  value={salesOwner}
                  onChange={(event) => setSalesOwner(event.target.value)}
                  placeholder="Name or email"
                />
                <button
                  type="button"
                  className="dealer-profile__btn dealer-profile__btn--primary"
                  disabled={savingOwner}
                  onClick={saveSalesOwner}
                >
                  {savingOwner ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : (
            <p className="dealer-profile__muted">Assign a representative after creating the CRM account.</p>
          )}
        </section>

        <section className="dealer-profile__card dealer-profile__card--wide">
          <h2>Account notes</h2>
          {crmDealerId ? (
            <>
              <form className="dealer-profile__note-form" onSubmit={submitNote}>
                <div className="dealer-profile__note-toolbar">
                  <select value={noteType} onChange={(event) => setNoteType(event.target.value)}>
                    {NOTE_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                  <button
                    type="submit"
                    className="dealer-profile__btn dealer-profile__btn--primary"
                    disabled={savingNote || !noteBody.trim()}
                  >
                    {savingNote ? "Saving…" : "Add note"}
                  </button>
                </div>
                <textarea
                  value={noteBody}
                  onChange={(event) => setNoteBody(event.target.value)}
                  placeholder="Leave an internal note for this dealer account…"
                  rows={4}
                />
              </form>
              <ul className="dealer-profile__notes">
                {(profile?.notes || []).length ? (
                  profile.notes.map((note) => (
                    <li key={note.id} className="dealer-profile__note">
                      <div className="dealer-profile__note-meta">
                        <StatusBadge tone="neutral">{note.noteType || "general"}</StatusBadge>
                        <span>{formatDate(note.createdAt)}</span>
                      </div>
                      <p>{note.body}</p>
                    </li>
                  ))
                ) : (
                  <li className="dealer-profile__muted">No notes yet.</li>
                )}
              </ul>
            </>
          ) : (
            <p className="dealer-profile__muted">Notes are available once this account has a CRM record.</p>
          )}
        </section>

        <section className="dealer-profile__card dealer-profile__card--wide">
          <h2>Locations & sources</h2>
          <div className="dealer-profile__split">
            <div>
              <h3>Locations ({account?.locationCount || 0})</h3>
              <ul className="dealer-profile__simple-list">
                {(account?.locations || []).slice(0, 8).map((location) => (
                  <li key={location.id}>
                    <strong>{location.name}</strong>
                    <span>
                      {location.city || "—"}
                      {location.isPrimary ? " · Primary" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>Top sources ({account?.dataSourceCount || 0})</h3>
              <ul className="dealer-profile__simple-list">
                {(account?.dataSources || []).slice(0, 8).map((source) => (
                  <li key={source.id}>
                    <strong>{source.label}</strong>
                    <span>
                      {source.typeLabel || source.type} · {source.listingCount || 0} listings
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
